import React, { lazy, Suspense, useEffect, useState, useMemo, useRef } from "react";
import { useToast } from "../../components/ui/Toast";
import { useParams, useSearchParams, Link } from "react-router-dom";
import {
  ArrowLeft, Plus, Play, CheckCircle, CheckCircle2, Clock, AlertTriangle, XCircle,
  Eye, EyeOff, RotateCcw, Trash2, Check, Loader2,
  FileText, Sparkles, BookOpen, Layers, AlertCircle,
  Edit2, Copy, Download, Lock, GraduationCap
} from "lucide-react";
import {
  api,
  type AdminCourseDetailResponse,
  type AdminCourseListItem,
  type AdminCourseModuleItem,
  type AdminCourseExamItem,
  type AdminCourseQuizItem,
  type AdminCourseNoteItem,
  type AdminCourseReviewItem,
  type AdminCourseOrderItem,
  type AdminCourseOrderChapter,
  type University,
  type SavedExtractionStatusResponse,
} from "../../lib/api";
import { useAdminToken } from "./adminShared";
import { Modal } from "../../components/ui/Modal";
import { ConfirmDialog } from "../../components/admin/AdminModals";
import { CourseStudentPreviewModal } from "../../components/admin/CourseStudentPreviewModal";
import { CourseResourceTable } from "../../components/admin/CourseResourceTable";
import { CourseQuizTable } from "../../components/admin/CourseQuizTable";
const CourseChaptersPanel = lazy(() => import("../../components/admin/CourseChaptersPanel").then((m) => ({ default: m.CourseChaptersPanel })));
import { StatusMessage, mapApiError } from "../../components/ui/StatusMessage";
import {
  buildExtractorPrompt,
  validateImportJson,
  type ValidationResult,
} from "../../lib/extractorPrompt";

type TabKey = "modules" | "exams" | "quizzes" | "chapters" | "reviews";
/** Old links and bookmarks used ?tab=notes; Notes and Chapters are one tab now. */
const TAB_ALIASES: Record<string, TabKey> = { notes: "chapters", chapters: "chapters", modules: "modules", exams: "exams", quizzes: "quizzes", reviews: "reviews", review: "reviews" };

export function AdminCourseDetailPage() {
  const { showToast } = useToast();
  const { courseId = "" } = useParams<{ courseId: string }>();
  const token = useAdminToken();

  const [data, setData] = useState<AdminCourseDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown | null>(null);
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<TabKey>(TAB_ALIASES[searchParams.get("tab") ?? ""] ?? "modules");

  // Modals state
  const [addResourceOpen, setAddResourceOpen] = useState(false);
  const [addResourceInitialTab, setAddResourceInitialTab] = useState<"module" | "import" | "manual_quiz">("module");
  const [addResourceInitialImportType, setAddResourceInitialImportType] = useState<"past_exam" | "quiz" | "coc" | "notes" | "content">("past_exam");
  const [generateModule, setGenerateModule] = useState<AdminCourseModuleItem | null>(null);
  const [confirmOrder, setConfirmOrder] = useState<AdminCourseOrderItem | null>(null);
  const [previewReviewItem, setPreviewReviewItem] = useState<AdminCourseReviewItem | null>(null);
  const [approveAnywayItem, setApproveAnywayItem] = useState<AdminCourseReviewItem | null>(null);
  const [deleteConfirmItem, setDeleteConfirmItem] = useState<{
    id: string;
    itemType: string;
    title: string;
    isDraft?: boolean;
    isResourceDelete?: boolean;
  } | null>(null);
  const [editItem, setEditItem] = useState<{
    id: string;
    itemType: string;
    title: string;
    difficulty?: string;
    markdown?: string;
    examYear?: string;
    isFree?: boolean;
  } | null>(null);
  const [rawModalItem, setRawModalItem] = useState<{
    title: string;
    type: string;
    rawUrl?: string | null;
    markdown?: string;
    questions?: any[];
  } | null>(null);
  const [studentPreview, setStudentPreview] = useState<Parameters<typeof CourseStudentPreviewModal>[0]["preview"] | null>(null);

  // Action loading states
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Multi-select & Bulk access state
  const [selectedItems, setSelectedItems] = useState<{ id: string; itemType: string; title: string }[]>([]);
  const [bulkAccessConfirm, setBulkAccessConfirm] = useState<{ isFree: boolean; count: number } | null>(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [successNotice, setSuccessNotice] = useState<string | null>(null);

  const isCoursePremium = Boolean(data?.course?.isPremium ?? data?.course?.is_premium ?? false);

  const isItemSelected = (id: string) => selectedItems.some((i) => i.id === id);

  const toggleSelectItem = (item: { id: string; itemType: string; title: string }) => {
    setSelectedItems((prev) => {
      const exists = prev.some((i) => i.id === item.id);
      if (exists) {
        return prev.filter((i) => i.id !== item.id);
      } else {
        return [...prev, item];
      }
    });
  };

  const handleSelectAllInTab = (items: { id: string; itemType: string; title: string }[]) => {
    const allSelected = items.length > 0 && items.every((i) => selectedItems.some((s) => s.id === i.id));
    if (allSelected) {
      const idsToRemove = new Set(items.map((i) => i.id));
      setSelectedItems((prev) => prev.filter((i) => !idsToRemove.has(i.id)));
    } else {
      const existingIds = new Set(selectedItems.map((i) => i.id));
      const toAdd = items.filter((i) => !existingIds.has(i.id));
      setSelectedItems((prev) => [...prev, ...toAdd]);
    }
  };

  const executeBulkAccess = async () => {
    if (!token || !courseId || !bulkAccessConfirm || selectedItems.length === 0) return;
    setBulkBusy(true);
    try {
      const payload = selectedItems.map((i) => ({ item_id: i.id, item_type: i.itemType }));
      const res = await api.admin.bulkSetCourseItemAccess(token, courseId, payload, bulkAccessConfirm.isFree);
      setSuccessNotice(res.message || `${res.count} items updated.`);
      setTimeout(() => setSuccessNotice(null), 4000);
      setSelectedItems([]);
      setBulkAccessConfirm(null);
      await fetchData();
    } catch (err) {
      showToast("error", mapApiError(err).message);
    } finally {
      setBulkBusy(false);
    }
  };

  const renderAccessControl = (itemType: string, itemId: string, isFree?: boolean) => {
    if (isCoursePremium) {
      return (
        <div
          className="inline-flex items-center gap-1.5"
          title="Effective status: Premium (Reason: Premium by course). When a course is Premium, all its resources are effectively premium for students."
        >
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-bold border bg-warning/15 text-warning border-warning/30 cursor-not-allowed opacity-90">
            <Lock size={11} /> PRO
          </span>
          <span className="text-xs text-muted-foreground italic hidden lg:inline">
            (Premium by course)
          </span>
        </div>
      );
    }

    const effectiveFree = isFree !== false;
    return (
      <div className="inline-flex items-center gap-1.5">
        <button
          type="button"
          onClick={() => handleToggleAccess(itemType, itemId, isFree)}
          disabled={actionLoading === `acc-${itemId}`}
          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-bold border transition cursor-pointer ${
            effectiveFree
              ? "bg-info/10 text-info border-info/30 hover:bg-info/20"
              : "bg-warning/15 text-warning border-warning/30 hover:bg-warning/25"
          }`}
          title={
            effectiveFree
              ? "Effective status: Free (Item setting). Click to make Premium."
              : "Effective status: Premium (Item setting). Click to make Free."
          }
        >
          {effectiveFree ? "Free" : "PRO"}
        </button>
      </div>
    );
  };

  const renderBulkBar = (tabItems: { id: string; itemType: string; title: string }[]) => {
    const tabSelectedCount = tabItems.filter((i) => isItemSelected(i.id)).length;
    const allSelected = tabItems.length > 0 && tabSelectedCount === tabItems.length;

    return (
      <div className="flex items-center justify-between gap-3 bg-muted/60 p-2.5 rounded-lg border border-border text-xs mb-3">
        <label className="flex items-center gap-2 cursor-pointer select-none font-semibold text-foreground">
          <input
            type="checkbox"
            checked={allSelected}
            onChange={() => handleSelectAllInTab(tabItems)}
            className="rounded border-border text-primary focus:ring-primary w-4 h-4 cursor-pointer"
          />
          <span>Select all in {activeTab} ({tabItems.length})</span>
        </label>

        {selectedItems.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-muted-foreground font-semibold">
              {selectedItems.length} selected
            </span>
            <button
              type="button"
              onClick={() => setBulkAccessConfirm({ isFree: true, count: selectedItems.length })}
              className="px-2.5 py-1 rounded bg-info/15 text-info border border-info/30 font-semibold hover:bg-info/25 transition cursor-pointer"
            >
              Set Free
            </button>
            <button
              type="button"
              onClick={() => setBulkAccessConfirm({ isFree: false, count: selectedItems.length })}
              className="px-2.5 py-1 rounded bg-warning/15 text-warning border border-warning/30 font-semibold hover:bg-warning/25 transition cursor-pointer"
            >
              Set Premium
            </button>
            <button
              type="button"
              onClick={() => setSelectedItems([])}
              className="px-2 py-1 text-muted-foreground hover:text-foreground font-semibold cursor-pointer"
            >
              Clear
            </button>
          </div>
        )}
      </div>
    );
  };

  const fetchData = async () => {
    if (!token || !courseId) return;
    try {
      setLoading(true);
      setError(null);
      const res = await api.admin.getCourseDetail(token, courseId, true);
      setData(res);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [token, courseId]);

  // Visibility toggle
  const handleToggleVisibility = async (itemType: string, itemId: string, isHidden: boolean) => {
    if (!token || !courseId) return;
    try {
      setActionLoading(`vis-${itemId}`);
      await api.admin.toggleCourseItemVisibility(token, courseId, itemType, itemId, !isHidden);
      await fetchData();
    } catch (err) {
      showToast("error", mapApiError(err).message);
    } finally {
      setActionLoading(null);
    }
  };

  // Free / Premium toggle
  const handleToggleAccess = async (itemType: string, itemId: string, isFree?: boolean) => {
    if (!token || !courseId) return;
    try {
      setActionLoading(`acc-${itemId}`);
      await api.admin.toggleCourseItemAccess(token, courseId, itemType, itemId, isFree !== undefined ? !isFree : undefined);
      await fetchData();
    } catch (err) {
      showToast("error", mapApiError(err).message);
    } finally {
      setActionLoading(null);
    }
  };

  // Edit item save
  const handleEditSave = async (payload: { title: string; difficulty?: string; markdown?: string; examYear?: string; is_free: boolean }) => {
    if (!token || !courseId || !editItem) return;
    try {
      setActionLoading(`edit-${editItem.id}`);
      await api.admin.editCourseItem(token, courseId, editItem.itemType, editItem.id, payload);
      setEditItem(null);
      await fetchData();
    } catch (err) {
      showToast("error", mapApiError(err).message);
    } finally {
      setActionLoading(null);
    }
  };

  // Approval
  const handleApprove = async (item: AdminCourseReviewItem, force = false) => {
    if (!token || !courseId) return;
    if (item.hasBlocking && !force) {
      setApproveAnywayItem(item);
      return;
    }
    try {
      setActionLoading(`app-${item.id}`);
      await api.admin.approveCourseItem(token, courseId, item.itemType, item.id, force);
      setApproveAnywayItem(null);
      if (previewReviewItem?.id === item.id) setPreviewReviewItem(null);
      await fetchData();
    } catch (err) {
      showToast("error", mapApiError(err).message);
    } finally {
      setActionLoading(null);
    }
  };

  // Deletion
  const handleDelete = async () => {
    if (!token || !courseId || !deleteConfirmItem) return;
    try {
      setActionLoading(`del-${deleteConfirmItem.id}`);
      if (deleteConfirmItem.isResourceDelete) {
        await api.admin.deleteResource(token, deleteConfirmItem.id);
      } else {
        await api.admin.deleteCourseItem(
          token,
          courseId,
          deleteConfirmItem.itemType,
          deleteConfirmItem.id,
          deleteConfirmItem.isDraft
        );
      }
      setDeleteConfirmItem(null);
      if (previewReviewItem?.id === deleteConfirmItem.id) setPreviewReviewItem(null);
      await fetchData();
    } catch (err) {
      showToast("error", mapApiError(err).message);
    } finally {
      setActionLoading(null);
    }
  };

  // Order actions
  const handleCancelOrder = async (order: AdminCourseOrderItem) => {
    if (!token || !courseId) return;
    try {
      setActionLoading(`ord-cancel-${order.id}`);
      await api.admin.cancelCourseOrder(token, courseId, order.moduleId, order.id);
      await fetchData();
    } catch (err) {
      showToast("error", mapApiError(err).message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleResumeOrder = async (order: AdminCourseOrderItem) => {
    if (!token || !courseId) return;
    try {
      setActionLoading(`ord-resume-${order.id}`);
      await api.admin.resumeCourseOrder(token, courseId, order.moduleId, order.id);
      await fetchData();
    } catch (err) {
      showToast("error", mapApiError(err).message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleRetryUnit = async (order: AdminCourseOrderItem, unitId: string) => {
    if (!token || !courseId) return;
    try {
      setActionLoading(`unit-retry-${unitId}`);
      await api.admin.retryCourseOrderUnit(token, courseId, order.moduleId, order.id, unitId);
      await fetchData();
    } catch (err) {
      showToast("error", mapApiError(err).message);
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-20 text-muted-foreground text-sm">
        <Loader2 size={20} className="animate-spin mr-2 text-primary" /> Loading course details…
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="py-8">
        <StatusMessage
          {...mapApiError(error || "Course not found")}
          action={{ label: "Go to Courses", href: "/admin/courses" }}
        />
      </div>
    );
  }

  const { course, modules, exams, quizzes, notes, reviews, orders, monthlyUsage } = data;
  const ingestPending = data.ingestPending ?? 0;
  const reviewCount = reviews.length + ingestPending;
  const isUnassigned = course.id === "unassigned";

  return (
    <div className="space-y-6">
      {/* Top Header */}
      <div>
        <Link
          to="/admin/courses"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-muted-foreground hover:text-foreground mb-3 transition-colors"
        >
          <ArrowLeft size={13} />
          Back to Courses
        </Link>

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-mono font-bold uppercase tracking-wider px-2 py-0.5 rounded-xs bg-primary/10 text-primary border border-primary/20">
                {course.code || "COURSE"}
              </span>
              <h1 className="text-2xl font-display font-bold text-foreground">{course.name}</h1>
              {!isUnassigned && (
                <>
                  {course.streamName && (
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20">
                      {course.streamName}
                    </span>
                  )}
                  {course.semester ? (
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-secondary text-foreground border border-border">
                      Semester {course.semester}
                    </span>
                  ) : (
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-warning/15 text-warning border border-warning/30 flex items-center gap-1">
                      <AlertTriangle size={12} /> No semester set
                    </span>
                  )}
                  {isCoursePremium ? (
                    <span className="text-xs font-bold px-2.5 py-0.5 rounded-full bg-warning/15 text-warning border border-warning/30 flex items-center gap-1">
                      <Lock size={11} /> Course Premium
                    </span>
                  ) : (
                    <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-secondary text-muted-foreground border border-border">
                      Standard Course
                    </span>
                  )}
                </>
              )}
            </div>
            {course.description && (
              <p className="text-xs sm:text-sm text-muted-foreground mt-1 max-w-2xl leading-relaxed">
                {course.description}
              </p>
            )}
            {isCoursePremium && (
              <div className="mt-2.5 p-2.5 rounded-lg border border-warning/30 bg-warning/10 text-warning text-xs flex items-center gap-2 max-w-2xl font-medium">
                <Lock size={14} className="shrink-0" />
                <span>
                  This course is marked as <strong>Premium</strong>. All materials below are effectively premium for students without an entitlement. Individual item controls are locked to "Premium by course".
                </span>
              </div>
            )}
            {!isUnassigned && !course.semester && (
              <div className="mt-2.5 p-2.5 rounded-lg border border-warning/30 bg-warning/10 text-warning text-xs flex items-center gap-2 max-w-2xl font-medium">
                <AlertTriangle size={14} className="shrink-0" />
                <span>
                  This course has no semester assigned. Students filtering by semester in the Study Hub will not see this course until a semester is set in the Catalog.
                </span>
              </div>
            )}
          </div>

          {!isUnassigned && (
            <div className="flex items-center gap-2">
              <Link
                to={`/admin/manual-extraction?courseId=${encodeURIComponent(course.id)}`}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-secondary text-foreground text-xs font-bold hover:bg-secondary/80 border border-border transition shrink-0 shadow-xs cursor-pointer"
                title="Download manual extraction instruction templates or upload/paste offline AI outputs"
              >
                <FileText size={14} />
                <span>Manual Extraction</span>
              </Link>
              <button
                type="button"
                onClick={() => setAddResourceOpen(true)}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-md bg-primary text-on-accent text-xs font-bold hover:opacity-90 transition shrink-0 shadow-xs cursor-pointer"
              >
                <Plus size={15} />
                Add resource
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Monthly Usage Summary Banner */}
      {monthlyUsage && (
        <div className="surface-sm rounded-lg p-4 border border-border flex flex-col md:flex-row md:items-center justify-between gap-4 shadow-xs">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-md bg-primary/10 text-primary flex items-center justify-center shrink-0">
              <Sparkles size={18} />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Monthly Pipeline Usage</p>
              <div className="flex items-baseline gap-2 mt-0.5">
                <span className="text-base font-bold text-foreground">
                  {monthlyUsage.monthlyTokens.toLocaleString()} tokens
                </span>
                <span className="text-xs text-muted-foreground">
                  ({monthlyUsage.estimatedCost} est.)
                </span>
              </div>
            </div>
          </div>

          <div className="flex-1 max-w-xs space-y-1.5">
            <div className="flex items-center justify-between text-xs font-medium">
              <span className="text-muted-foreground">Monthly budget limit</span>
              <span className="text-foreground font-semibold">{monthlyUsage.budgetPct}%</span>
            </div>
            <div className="w-full bg-secondary h-2 rounded-full overflow-hidden">
              <div
                className={`h-full transition-all duration-300 rounded-full ${monthlyUsage.budgetPct >= 100
                  ? "bg-destructive"
                  : monthlyUsage.budgetPct >= 80
                    ? "bg-warning"
                    : "bg-primary"
                  }`}
                style={{ width: `${Math.min(monthlyUsage.budgetPct, 100)}%` }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Order Management Panel */}
      {orders && orders.length > 0 && (
        <div className="surface-sm rounded-lg border border-border p-4 space-y-3">
          <div className="flex items-center justify-between border-b border-border/60 pb-2.5">
            <h3 className="text-sm font-bold text-foreground flex items-center gap-2">
              <Sparkles size={14} className="text-primary" /> Extraction Orders ({orders.length})
            </h3>
          </div>

          <div className="space-y-3">
            {orders.map((order) => {
              const isAwaiting = order.state === "awaiting_confirmation";
              const isRunning = order.state === "running";
              const isPaused = order.state.startsWith("paused");
              const isCompleted = order.state.startsWith("completed");

              return (
                <div
                  key={order.id}
                  className="bg-card border border-border rounded-md p-3.5 space-y-2.5 text-xs shadow-xs"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold text-foreground">{order.moduleTitle}</span>
                      <span className="text-muted-foreground">·</span>
                      <span className="font-medium text-muted-foreground">{order.progress}</span>

                      {/* State Badge */}
                      <span className={`px-2 py-0.5 rounded-full font-bold uppercase tracking-wider text-xs border ${isAwaiting
                        ? "bg-warning/15 text-warning border-warning/30 animate-pulse"
                        : isRunning
                          ? "bg-info/15 text-info border-info/30"
                          : isPaused
                            ? "bg-destructive/15 text-destructive border-destructive/30"
                            : isCompleted
                              ? "bg-success/15 text-success border-success/30"
                              : "bg-secondary text-muted-foreground border-border"
                        }`}>
                        {order.state}
                      </span>
                    </div>

                    {/* Order action button */}
                    <div className="flex items-center gap-2 shrink-0">
                      {isAwaiting && (
                        <button
                          type="button"
                          onClick={() => setConfirmOrder(order)}
                          className="px-3 py-1 rounded-md bg-warning text-on-accent font-bold hover:opacity-90 transition cursor-pointer"
                        >
                          Confirm chapters
                        </button>
                      )}

                      {isRunning && (
                        <button
                          type="button"
                          onClick={() => handleCancelOrder(order)}
                          disabled={actionLoading === `ord-cancel-${order.id}`}
                          className="px-3 py-1 rounded-md bg-destructive/10 text-destructive border border-destructive/20 font-bold hover:bg-destructive/20 transition cursor-pointer"
                        >
                          {actionLoading === `ord-cancel-${order.id}` ? "Cancelling…" : "Cancel"}
                        </button>
                      )}

                      {isPaused && order.actionText === "Resume" && (
                        <button
                          type="button"
                          onClick={() => handleResumeOrder(order)}
                          disabled={actionLoading === `ord-resume-${order.id}`}
                          className="px-3 py-1 rounded-md bg-primary text-on-accent font-bold hover:opacity-90 transition cursor-pointer"
                        >
                          {actionLoading === `ord-resume-${order.id}` ? "Resuming…" : "Resume"}
                        </button>
                      )}

                      {isCompleted && (
                        <button
                          type="button"
                          onClick={() => setActiveTab("reviews")}
                          className="px-3 py-1 rounded-md bg-primary/10 text-primary border border-primary/20 font-bold hover:bg-primary/20 transition cursor-pointer"
                        >
                          Open review
                        </button>
                      )}
                    </div>
                  </div>

                  {/* State Text & Explanation */}
                  <div className="flex flex-wrap items-center justify-between gap-2 text-muted-foreground bg-secondary/50 rounded-xs p-2">
                    <p className="font-medium text-foreground">{order.stateText}</p>
                    <div className="flex items-center gap-3">
                      <span>Notes: <strong className="text-foreground">{order.counts.notes}</strong></span>
                      <span>Questions: <strong className="text-foreground">{order.counts.questions}</strong></span>
                      <span>To review: <strong className="text-foreground">{order.counts.toReview}</strong></span>
                      <span>Tokens: <strong className="text-foreground">{order.tokens.toLocaleString()}</strong> ({order.estimatedCost})</span>
                    </div>
                  </div>

                  {/* Per-chapter Retry for review_required units */}
                  {order.reviewUnits && order.reviewUnits.length > 0 && (
                    <div className="pt-2 border-t border-border/60 space-y-1.5">
                      <p className="font-semibold text-warning flex items-center gap-1.5">
                        <AlertTriangle size={12} /> Units requiring review:
                      </p>
                      {order.reviewUnits.map((u) => (
                        <div key={u.id} className="flex items-center justify-between bg-warning/10 border border-warning/20 rounded-xs px-2.5 py-1.5">
                          <span className="text-foreground font-mono">Stage: {u.stage} — {u.error}</span>
                          <button
                            type="button"
                            onClick={() => handleRetryUnit(order, u.id)}
                            disabled={actionLoading === `unit-retry-${u.id}`}
                            className="inline-flex items-center gap-1 text-primary hover:underline font-bold"
                          >
                            <RotateCcw size={11} /> {actionLoading === `unit-retry-${u.id}` ? "Retrying…" : "Retry"}
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Main Tabs Navigation */}
      <div className="border-b border-border">
        <div className="flex items-center gap-2 overflow-x-auto pb-px">
          {(
            [
              { key: "modules", label: "Modules", count: modules.length, badgeColor: undefined },
              { key: "exams", label: "Past exams", count: exams.length, badgeColor: undefined },
              { key: "quizzes", label: "Quizzes", count: quizzes.length, badgeColor: undefined },
              { key: "chapters", label: "Chapters", count: data.chaptersCount, badgeColor: undefined },
              { key: "reviews", label: "Review", count: reviewCount, badgeColor: reviewCount > 0 ? "warning" : undefined },
            ] as const
          ).map((tab) => {
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                className={`inline-flex items-center gap-1.5 px-3.5 py-2.5 text-xs font-bold border-b-2 transition whitespace-nowrap cursor-pointer ${isActive
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground hover:border-border"
                  }`}
              >
                <span>{tab.label}</span>
                {tab.count !== undefined && (
                  <span className={`px-1.5 py-0.2 rounded-full text-xs font-bold ${tab.badgeColor === "warning" && tab.count > 0
                    ? "bg-warning/20 text-warning border border-warning/30"
                    : isActive
                      ? "bg-primary/10 text-primary"
                      : "bg-muted text-muted-foreground"
                    }`}>
                    {tab.count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab 1: Modules */}
      {activeTab === "modules" && token && (
        <CourseResourceTable
          kind="module"
          items={modules}
          token={token}
          courseId={courseId}
          isCoursePremium={isCoursePremium}
          isUnassigned={isUnassigned}
          onChanged={fetchData}
          onEdit={(m) => setEditItem({ id: m.id, itemType: (m as AdminCourseModuleItem).resourceType, title: m.title, isFree: m.isFree })}
          onPreview={(m) => setStudentPreview({ kind: "module", item: m as AdminCourseModuleItem })}
          onGenerate={(m) => setGenerateModule(m)}
          onToggleVisibility={(m) => handleToggleVisibility((m as AdminCourseModuleItem).resourceType, m.id, m.isHidden)}
          onToggleAccess={(m) => handleToggleAccess((m as AdminCourseModuleItem).resourceType, m.id, m.isFree)}
          onDeleteForever={(m) => setDeleteConfirmItem({ id: m.id, itemType: (m as AdminCourseModuleItem).resourceType, title: m.title, isResourceDelete: true })}
          onAdd={() => setAddResourceOpen(true)}
          addLabel="Upload module"
        />
      )}

      {/* Tab 2: Past Exams */}
      {activeTab === "exams" && token && (
        <CourseResourceTable
          kind="exam"
          items={exams}
          token={token}
          courseId={courseId}
          isCoursePremium={isCoursePremium}
          isUnassigned={isUnassigned}
          onChanged={fetchData}
          onEdit={(e) => setEditItem({ id: e.id, itemType: "exam", title: e.title, examYear: (e as AdminCourseExamItem).examYear, isFree: e.isFree })}
          onPreview={(e) => setStudentPreview({ kind: "exam", item: e as AdminCourseExamItem })}
          onToggleVisibility={(e) => handleToggleVisibility("exam", e.id, e.isHidden)}
          onToggleAccess={(e) => handleToggleAccess("exam", e.id, e.isFree)}
          onDeleteForever={(e) => setDeleteConfirmItem({ id: e.id, itemType: "exam", title: e.title, isResourceDelete: true })}
          onAdd={() => {
            setAddResourceInitialTab("import");
            setAddResourceInitialImportType("past_exam");
            setAddResourceOpen(true);
          }}
          addLabel="Import exam"
        />
      )}

      {/* Tab 3: Quizzes */}
      {activeTab === "quizzes" && token && (
        <CourseQuizTable
          items={quizzes}
          token={token}
          courseId={courseId}
          isCoursePremium={isCoursePremium}
          isUnassigned={isUnassigned}
          onChanged={fetchData}
          onEdit={(q) => setEditItem({ id: q.id, itemType: "quiz", title: q.title, difficulty: q.difficulty, isFree: q.isFree })}
          onPreview={(q) => setStudentPreview({ kind: "quiz", item: q })}
          onRaw={(q) => setRawModalItem({ title: q.title, type: "quiz", questions: q.questions })}
          onToggleVisibility={(q) => handleToggleVisibility("quiz", q.id, q.isHidden)}
          onToggleAccess={(q) => handleToggleAccess("quiz", q.id, q.isFree)}
          onDelete={(q) => setDeleteConfirmItem({ id: q.id, itemType: "quiz", title: q.title })}
          onAdd={() => {
            setAddResourceInitialTab("import");
            setAddResourceInitialImportType("quiz");
            setAddResourceOpen(true);
          }}
        />
      )}

      {/* Tab 4: Chapters (the single place for notes) */}
      {activeTab === "chapters" && token && (
        <Suspense fallback={<div className="h-40 animate-pulse rounded-card bg-soft" />}>
          <CourseChaptersPanel token={token} courseId={courseId} />
        </Suspense>
      )}

      {/* Older notes (previous importer): kept under Chapters so nothing the old Notes tab could do is lost */}
      {activeTab === "chapters" && notes.length > 0 && (
        <details className="space-y-3">
          <summary className="min-h-11 cursor-pointer py-2 text-sm font-semibold">Older notes ({notes.length})</summary>
          {(
            <div className="space-y-3">
              {renderBulkBar(notes.map((n) => ({ id: n.id, itemType: n.kind, title: n.title })))}
              <div className="bg-card border border-border rounded-lg overflow-hidden shadow-xs divide-y divide-border">
                {notes.map((n) => (
                  <div key={n.id} className="p-3.5 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs">
                    <div className="flex items-start gap-3 min-w-0">
                      <input
                        type="checkbox"
                        checked={isItemSelected(n.id)}
                        onChange={() => toggleSelectItem({ id: n.id, itemType: n.kind, title: n.title })}
                        className="rounded border-border text-primary focus:ring-primary w-4 h-4 cursor-pointer mt-2 shrink-0"
                        title="Select for bulk action"
                      />
                      <div className="w-8 h-8 rounded-md bg-primary/10 text-primary flex items-center justify-center shrink-0 mt-0.5">
                        <BookOpen size={16} />
                      </div>
                      <div>
                        <h4 className="font-bold text-foreground text-sm">{n.title}</h4>
                        <p className="text-muted-foreground mt-0.5">
                          {n.moduleTitle} · {n.chapterTitle} · [{n.kind}] · {n.date}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 shrink-0 self-end sm:self-center flex-wrap">
                      <StatusBadge status={n.status} />
                      {n.source === "manual" ? (
                        <span className="px-1.5 py-0.5 rounded text-xs font-bold bg-accent/15 text-accent border border-accent/30" title="Manually extracted / uploaded">
                          Manual
                        </span>
                      ) : (
                        <span className="px-1.5 py-0.5 rounded text-xs font-bold bg-primary/10 text-primary border border-primary/20" title="AI pipeline generated">
                          AI
                        </span>
                      )}

                      {/* Free / Premium Control */}
                      {renderAccessControl(n.kind, n.id, n.isFree)}

                    {/* Student View Button */}
                    {n.studentUrl && (
                      <button
                        type="button"
                        onClick={() => setStudentPreview({ kind: "note", item: n })}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold bg-secondary text-foreground border border-border hover:bg-secondary/80 transition"
                        title="View how students see this note"
                      >
                        <Eye size={11} className="text-primary" />
                        <span>Student View</span>
                      </button>
                    )}

                    {/* Raw Markdown Button */}
                    <button
                      type="button"
                      onClick={() => setRawModalItem({ title: n.title, type: "note", markdown: n.markdown })}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold bg-secondary text-foreground border border-border hover:bg-secondary/80 transition cursor-pointer"
                      title="View raw markdown material"
                    >
                      <FileText size={11} />
                      <span>Raw</span>
                    </button>

                    {/* Visibility Switch */}
                    {n.isPublished && (
                      <button
                        type="button"
                        onClick={() => handleToggleVisibility(n.kind, n.id, n.isHidden)}
                        disabled={actionLoading === `vis-${n.id}`}
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold border transition cursor-pointer ${n.isHidden
                            ? "bg-secondary text-muted-foreground border-border hover:text-foreground"
                            : "bg-success/10 text-success border-success/30 hover:bg-success/20"
                          }`}
                        title={n.isHidden ? "Hidden from students. Click to make visible." : "Visible to students. Click to hide."}
                      >
                        {n.isHidden ? <EyeOff size={11} /> : <Eye size={11} />}
                        {n.isHidden ? "Hidden" : "Visible"}
                      </button>
                    )}

                    {/* Edit button */}
                    <button
                      type="button"
                      onClick={() => setEditItem({ id: n.id, itemType: n.kind, title: n.title, markdown: n.markdown, isFree: n.isFree })}
                      className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-secondary transition cursor-pointer"
                      title="Edit note"
                    >
                      <Edit2 size={13} />
                    </button>

                    {/* Delete button */}
                    <button
                      type="button"
                      onClick={() => setDeleteConfirmItem({ id: n.id, itemType: n.kind, title: n.title })}
                      className="p-1 rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition cursor-pointer"
                      title="Delete note"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
            </div>
          )}
        </details>
      )}

      {activeTab === "reviews" && (
        <div className="space-y-3">
          {ingestPending > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-tile bg-soft p-4 text-sm">
              <span>
                <span className="font-semibold">{ingestPending} {ingestPending === 1 ? "item is" : "items are"} waiting in Content review</span>
                <span className="block text-muted-foreground">Notes, quiz questions and exam questions from the content pipeline for this course.</span>
              </span>
              <Link to={`/admin/content-review?course=${encodeURIComponent(course.id)}`} className="inline-flex min-h-11 items-center rounded-btn bg-primary px-4 font-semibold text-on-accent focus-visible:ring-2 focus-visible:ring-ring">
                Open Content review
              </Link>
            </div>
          )}
          {reviews.length === 0 && ingestPending === 0 ? (
            <StatusMessage
              variant="info"
              title="Review queue empty"
              message="There are no drafts awaiting review for this course."
            />
          ) : reviews.length === 0 ? null : (
            <div className="space-y-3">
              {reviews.map((item) => (
                <div
                  key={item.id}
                  className="bg-card border border-border rounded-lg p-4 space-y-3 text-xs shadow-xs"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-border/60 pb-2.5">
                    <div>
                      <span className="font-mono font-bold text-xs uppercase tracking-wider text-muted-foreground">
                        {item.label}
                      </span>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="font-bold text-sm text-foreground">{item.previewData?.title || item.type}</span>
                        <span className="px-2 py-0.2 rounded-xs bg-muted text-muted-foreground uppercase text-xs font-semibold">
                          {item.type}
                        </span>
                        <span className="text-muted-foreground">· Source: {item.source} · {item.time}</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        type="button"
                        onClick={() => setPreviewReviewItem(item)}
                        className="px-2.5 py-1 rounded-md bg-secondary text-foreground hover:bg-secondary/80 font-semibold cursor-pointer"
                      >
                        Review
                      </button>

                      <button
                        type="button"
                        onClick={() => handleApprove(item)}
                        disabled={actionLoading === `app-${item.id}`}
                        className={`px-3 py-1 rounded-md font-bold text-on-accent transition cursor-pointer ${item.hasBlocking ? "bg-warning hover:opacity-90" : "bg-success hover:opacity-90"
                          }`}
                      >
                        {actionLoading === `app-${item.id}` ? "Approving…" : "Approve"}
                      </button>

                      <button
                        type="button"
                        onClick={() => setDeleteConfirmItem({ id: item.id, itemType: item.itemType, title: item.label, isDraft: true })}
                        className="p-1 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition cursor-pointer"
                        title="Delete draft"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </div>

                  {/* Validation Flags (Blocking vs Advisory First) */}
                  {item.flags && item.flags.length > 0 && (
                    <div className="space-y-1.5 pt-1">
                      {item.flags.map((flag, idx) => (
                        <div
                          key={idx}
                          className={`flex items-center gap-2 p-2 rounded-xs border text-xs ${flag.type === "blocking"
                            ? "bg-destructive/10 border-destructive/20 text-destructive font-semibold"
                            : "bg-warning/10 border-warning/20 text-warning"
                            }`}
                        >
                          {flag.type === "blocking" ? <XCircle size={14} className="shrink-0" /> : <AlertTriangle size={14} className="shrink-0" />}
                          <span className="font-bold uppercase tracking-wider text-xs">[{flag.type}]</span>
                          <span>{flag.text}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Modals */}

      {/* 1. Add Resource Modal (Course fixed) */}
      {addResourceOpen && (
        <AddResourceModal
          courseId={course.id}
          courseName={course.name}
          courseCode={course.code}
          courseType={course.courseType || (course as any).course_type || "LANGUAGE"}
          extractionProfile={course.extractionProfile || (course as any).extraction_profile || null}
          modules={modules}
          initialTab={addResourceInitialTab}
          initialImportType={addResourceInitialImportType}
          onClose={() => {
            setAddResourceOpen(false);
            setAddResourceInitialTab("module");
            setAddResourceInitialImportType("past_exam");
          }}
          onSuccess={() => {
            setAddResourceOpen(false);
            setAddResourceInitialTab("module");
            setAddResourceInitialImportType("past_exam");
            fetchData();
          }}
        />
      )}


      {/* 2. Generate Order Modal */}
      {generateModule && (
        <GenerateOrderModal
          courseId={course.id}
          module={generateModule}
          onClose={() => setGenerateModule(null)}
          onSuccess={() => {
            setGenerateModule(null);
            fetchData();
          }}
        />
      )}

      {/* 3. Confirm Chapters Modal */}
      {confirmOrder && (
        <ConfirmChaptersModal
          courseId={course.id}
          order={confirmOrder}
          onClose={() => setConfirmOrder(null)}
          onSuccess={() => {
            setConfirmOrder(null);
            fetchData();
          }}
        />
      )}

      {/* 4. Review Preview Modal */}
      {previewReviewItem && (
        <ReviewPreviewModal
          item={previewReviewItem}
          onClose={() => setPreviewReviewItem(null)}
          onApprove={() => handleApprove(previewReviewItem)}
          onDelete={() => {
            setDeleteConfirmItem({
              id: previewReviewItem.id,
              itemType: previewReviewItem.itemType,
              title: previewReviewItem.label,
              isDraft: true,
            });
          }}
        />
      )}

      {/* 5. Approve Anyway Modal */}
      {approveAnywayItem && (
        <Modal
          isOpen={true}
          onClose={() => setApproveAnywayItem(null)}
          title="Approve Anyway?"
          description="This item has blocking validation flags. Approving it will publish it despite warnings."
          size="sm"
        >
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              {approveAnywayItem.flags
                .filter((f) => f.type === "blocking")
                .map((f, i) => (
                  <div key={i} className="flex items-center gap-2 p-2 rounded-md bg-destructive/10 text-destructive text-xs">
                    <AlertTriangle size={14} className="shrink-0" />
                    <span>{f.text}</span>
                  </div>
                ))}
            </div>

            <div className="flex justify-end gap-2 pt-3 border-t border-border">
              <button
                type="button"
                onClick={() => setApproveAnywayItem(null)}
                className="px-3 py-1.5 rounded-md border border-border text-xs font-semibold hover:bg-secondary cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleApprove(approveAnywayItem, true)}
                className="px-3 py-1.5 rounded-md bg-destructive text-white text-xs font-bold hover:opacity-90 cursor-pointer"
              >
                Approve anyway
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* 6. Delete Confirmation Modal */}
      {deleteConfirmItem && (
        <Modal
          isOpen={true}
          onClose={() => setDeleteConfirmItem(null)}
          title={
            deleteConfirmItem.isDraft
              ? "Delete draft content?"
              : `Delete ${deleteConfirmItem.title}?`
          }
          description={
            deleteConfirmItem.isDraft &&
            (deleteConfirmItem.itemType === "module" ||
              deleteConfirmItem.itemType === "resource" ||
              deleteConfirmItem.itemType === "module_draft")
              ? `Are you sure you want to delete the draft extraction for ${deleteConfirmItem.title}? The main module file will be preserved.`
              : deleteConfirmItem.isResourceDelete
              ? `Are you sure you want to permanently delete ${deleteConfirmItem.title}? This will delete the resource file from the course.`
              : `Are you sure you want to delete ${deleteConfirmItem.title}? This action cannot be undone.`
          }
          size="sm"
        >
          <div className="flex justify-end gap-2 pt-4">
            <button
              type="button"
              onClick={() => setDeleteConfirmItem(null)}
              className="px-3 py-1.5 rounded-md border border-border text-xs font-semibold hover:bg-secondary cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleDelete}
              disabled={Boolean(actionLoading)}
              className="px-3 py-1.5 rounded-md bg-destructive text-white text-xs font-bold hover:opacity-90 cursor-pointer"
            >
              {actionLoading ? "Deleting…" : "Delete"}
            </button>
          </div>
        </Modal>
      )}

      {/* 7. Edit Course Item Modal */}
      {editItem && (
        <EditCourseItemModal
          item={editItem}
          loading={Boolean(actionLoading)}
          onClose={() => setEditItem(null)}
          onSave={handleEditSave}
        />
      )}

      {/* 8. Raw Material Preview Modal */}
      {rawModalItem && (
        <RawMaterialModal
          item={rawModalItem}
          onClose={() => setRawModalItem(null)}
        />
      )}

      {studentPreview && (
        <CourseStudentPreviewModal
          preview={studentPreview}
          onClose={() => setStudentPreview(null)}
        />
      )}

      {/* 9. Bulk Access Change Confirmation Dialog */}
      <ConfirmDialog
        open={Boolean(bulkAccessConfirm)}
        title={
          bulkAccessConfirm?.isFree
            ? `Set ${bulkAccessConfirm.count} items as Free?`
            : `Set ${bulkAccessConfirm?.count} items as Premium?`
        }
        message={
          bulkAccessConfirm?.isFree
            ? `Are you sure you want to set access to Free for the ${bulkAccessConfirm.count} selected item(s)? Free items can be accessed by all registered students unless the whole course is set to Premium.`
            : `Are you sure you want to set access to Premium for the ${bulkAccessConfirm?.count} selected item(s)? Premium items require an active semester entitlement.`
        }
        confirmText={bulkAccessConfirm?.isFree ? "Set Free" : "Set Premium"}
        variant="primary"
        loading={bulkBusy}
        onConfirm={executeBulkAccess}
        onClose={() => setBulkAccessConfirm(null)}
      />
    </div>
  );
}

function StatusBadge({ status }: { status: "Draft" | "Visible" | "Hidden" }) {
  if (status === "Draft") {
    return (
      <span className="px-2 py-0.5 rounded-full font-bold uppercase tracking-wider text-xs bg-warning/15 text-warning border border-warning/30">
        Draft
      </span>
    );
  }
  if (status === "Hidden") {
    return (
      <span className="px-2 py-0.5 rounded-full font-bold uppercase tracking-wider text-xs bg-secondary text-muted-foreground border border-border">
        Hidden
      </span>
    );
  }
  return (
    <span className="px-2 py-0.5 rounded-full font-bold uppercase tracking-wider text-xs bg-success/15 text-success border border-success/30">
      Visible
    </span>
  );
}

// ----------------------------------------------------------------------
// Sub-Modal: Add Resource (Course fixed, no course select)
// ----------------------------------------------------------------------
function AddResourceModal({
  courseId,
  courseName,
  courseCode,
  courseType,
  extractionProfile,
  modules,
  initialTab = "module",
  initialImportType = "past_exam",
  onClose,
  onSuccess,
}: {
  courseId: string;
  courseName: string;
  courseCode: string;
  courseType?: string;
  extractionProfile?: string | null;
  modules: AdminCourseModuleItem[];
  initialTab?: "module" | "import" | "manual_quiz";
  initialImportType?: "past_exam" | "quiz" | "coc" | "notes" | "content";
  onClose: () => void;
  onSuccess: () => void;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const token = useAdminToken();
  const [tab, setTab] = useState<"module" | "import" | "manual_quiz">(initialTab);
  const [title, setTitle] = useState("");
  const [resourceKind, setResourceKind] = useState<"module" | "handout">("module");
  const [file, setFile] = useState<File | null>(null);
  const [importType, setImportType] = useState<"past_exam" | "quiz" | "coc" | "notes" | "content">(initialImportType);
  const [selectedModuleId, setSelectedModuleId] = useState<string>("");
  const [jsonData, setJsonData] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Multi-Module Upload Queue State
  interface ModuleUploadQueueItem {
    id: string;
    file: File;
    title: string;
    kind: "module" | "handout";
  }
  const [moduleQueue, setModuleQueue] = useState<ModuleUploadQueueItem[]>([]);
  const [uploadProgressText, setUploadProgressText] = useState<string | null>(null);

  const handleModuleFilesSelected = (selectedFiles: FileList | null) => {
    if (!selectedFiles || selectedFiles.length === 0) return;
    const newItems: ModuleUploadQueueItem[] = Array.from(selectedFiles).map((f) => {
      const baseName = f.name.replace(/\.[^/.]+$/, "");
      const cleanTitle = baseName.replace(/[_-]+/g, " ").replace(/\s+/g, " ").trim();
      return {
        id: Math.random().toString(36).substring(2, 9),
        file: f,
        title: cleanTitle || "Untitled Module",
        kind: "module",
      };
    });
    setModuleQueue((prev) => [...prev, ...newItems]);
    setErrorMsg(null);
  };

  const handleUpdateQueueItem = (id: string, updates: Partial<ModuleUploadQueueItem>) => {
    setModuleQueue((prev) =>
      prev.map((item) => (item.id === id ? { ...item, ...updates } : item))
    );
  };

  const handleRemoveQueueItem = (id: string) => {
    setModuleQueue((prev) => prev.filter((item) => item.id !== id));
  };

  // Feature 3: Extractor Prompt Generator state
  const [coursesList, setCoursesList] = useState<AdminCourseListItem[]>([]);
  const [promptCourseId, setPromptCourseId] = useState<string>(courseId);
  const [examNameYear, setExamNameYear] = useState<string>("");
  const [rangeFrom, setRangeFrom] = useState<string>("");
  const [rangeTo, setRangeTo] = useState<string>("");
  const [promptCopied, setPromptCopied] = useState<boolean>(false);
  const [showPromptPanel, setShowPromptPanel] = useState<boolean>(true);

  useEffect(() => {
    if (!token) return;
    api.admin.listCourses(token).then((res) => {
      if (res?.items) setCoursesList(res.items);
    }).catch(() => {});
  }, [token]);

  // Resolve selected course in prompt generator
  const activeCourseObj = useMemo(() => {
    const found = coursesList.find((c) => c.id === promptCourseId);
    if (found) return found;
    return {
      id: courseId,
      name: courseName,
      code: courseCode,
      courseType: courseType || "LANGUAGE",
      extractionProfile: extractionProfile || null,
    };
  }, [coursesList, promptCourseId, courseId, courseName, courseCode, courseType, extractionProfile]);

  const activeProfileUsed = useMemo(() => {
    const prof = activeCourseObj.extractionProfile || (activeCourseObj as any).extraction_profile;
    if (prof && prof.trim()) return "Custom profile";
    const cType = (activeCourseObj.courseType || (activeCourseObj as any).course_type || "LANGUAGE").toUpperCase();
    return `Default: ${cType}`;
  }, [activeCourseObj]);

  const generatedPrompt = useMemo(() => {
    const titleToUse = examNameYear.trim() || title.trim() || `${activeCourseObj.name} Examination`;
    return buildExtractorPrompt(
      {
        name: activeCourseObj.name,
        code: activeCourseObj.code,
        course_type: activeCourseObj.courseType || (activeCourseObj as any).course_type || "LANGUAGE",
        extraction_profile: activeCourseObj.extractionProfile || (activeCourseObj as any).extraction_profile || null,
      },
      titleToUse,
      {
        mode: "PAST_EXAM",
        from: rangeFrom ? Number(rangeFrom) : undefined,
        to: rangeTo ? Number(rangeTo) : undefined,
      }
    );
  }, [activeCourseObj, examNameYear, title, rangeFrom, rangeTo]);

  const handleCopyPrompt = async () => {
    try {
      await navigator.clipboard.writeText(generatedPrompt);
      setPromptCopied(true);
      setTimeout(() => setPromptCopied(false), 2000);
    } catch {
      setErrorMsg("Failed to copy prompt to clipboard.");
    }
  };

  // Feature 4: Live import validation
  const validation = useMemo<ValidationResult | null>(() => {
    if (tab === "import" && ["past_exam", "quiz", "coc"].includes(importType) && jsonData.trim()) {
      return validateImportJson(jsonData);
    }
    return null;
  }, [tab, importType, jsonData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;
    setErrorMsg(null);

    // Multi-module upload handling
    if (tab === "module") {
      if (moduleQueue.length === 0) {
        setErrorMsg("Please select at least one PDF file to upload.");
        return;
      }
      for (let i = 0; i < moduleQueue.length; i++) {
        if (!moduleQueue[i].title.trim()) {
          setErrorMsg(`Module #${i + 1} (${moduleQueue[i].file.name}) requires a title.`);
          return;
        }
      }

      try {
        setSubmitting(true);
        for (let i = 0; i < moduleQueue.length; i++) {
          const item = moduleQueue[i];
          setUploadProgressText(`Uploading ${i + 1} of ${moduleQueue.length}: ${item.title}...`);
          const fd = new FormData();
          fd.append("title", item.title.trim());
          fd.append("resource_kind", item.kind);
          fd.append("file", item.file);
          await api.admin.addCourseResource(token, courseId, fd);
        }
        onSuccess();
      } catch (err: any) {
        setErrorMsg(err.message || "Failed during module upload.");
      } finally {
        setSubmitting(false);
        setUploadProgressText(null);
      }
      return;
    }

    if (!title.trim()) {
      setErrorMsg("Title is required.");
      return;
    }

    // Feature 4: Validate before saving
    if (tab === "import" && ["past_exam", "quiz", "coc"].includes(importType)) {
      if (validation && !validation.valid) {
        setErrorMsg(`Cannot import: Please resolve the ${validation.errors.length} validation error(s) first.`);
        return;
      }
    }

    try {
      setSubmitting(true);
      const fd = new FormData();
      fd.append("title", title.trim());

      if (tab === "import") {
        if (!jsonData.trim()) {
          setErrorMsg("Please paste JSON data.");
          setSubmitting(false);
          return;
        }
        fd.append("resource_kind", "import");
        fd.append("import_type", importType);
        if (selectedModuleId) fd.append("module_id", selectedModuleId);
        fd.append("json_data", jsonData);
      } else if (tab === "manual_quiz") {
        fd.append("resource_kind", "manual_quiz");
        if (selectedModuleId) fd.append("module_id", selectedModuleId);
        if (jsonData.trim()) fd.append("json_data", jsonData);
      }

      await api.admin.addCourseResource(token, courseId, fd);
      onSuccess();
    } catch (err: any) {
      setErrorMsg(err.message || "Failed to add resource");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title="Add Resource"
      description={`Adding to course: ${courseCode} · ${courseName}`}
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-4 pt-2">
        {/* Fixed Course Indicator */}
        <div className="p-2.5 rounded-md bg-secondary border border-border flex items-center justify-between text-xs">
          <span className="text-muted-foreground font-semibold">Selected Course (Fixed):</span>
          <span className="font-bold text-foreground">{courseCode} — {courseName}</span>
        </div>

        {/* Tab Selection */}
        <div className="grid grid-cols-3 gap-1.5 p-1 bg-secondary/80 rounded-md text-xs font-semibold">
          <button
            type="button"
            onClick={() => setTab("module")}
            className={`py-1.5 rounded-xs transition cursor-pointer ${
              tab === "module" ? "bg-card text-foreground shadow-xs font-bold" : "text-muted-foreground hover:text-foreground"
            }`}
          >
            Module / Handout
          </button>
          <button
            type="button"
            onClick={() => setTab("import")}
            className={`py-1.5 rounded-xs transition cursor-pointer ${
              tab === "import" ? "bg-card text-foreground shadow-xs font-bold" : "text-muted-foreground hover:text-foreground"
            }`}
          >
            JSON Import
          </button>
          <button
            type="button"
            onClick={() => setTab("manual_quiz")}
            className={`py-1.5 rounded-xs transition cursor-pointer ${
              tab === "manual_quiz" ? "bg-card text-foreground shadow-xs font-bold" : "text-muted-foreground hover:text-foreground"
            }`}
          >
            Manual Quiz
          </button>
        </div>

        {errorMsg && (
          <div className="p-2.5 rounded-md bg-destructive/10 text-destructive border border-destructive/20 text-xs flex items-center gap-2">
            <AlertCircle size={14} className="shrink-0" />
            <span>{errorMsg}</span>
          </div>
        )}

        {/* Common Title (Only for JSON Import and Manual Quiz tabs) */}
        {tab !== "module" && (
          <div>
            <label className="block text-xs font-semibold text-muted-foreground uppercase mb-1">Title *</label>
            <input
              type="text"
              required
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. 2016 Midterm Exam or Chapter Practice Quiz"
              className="field w-full text-xs px-3 py-2 rounded-md"
            />
          </div>
        )}

        {/* Tab 1: Multi-Module / Handout Upload */}
        {tab === "module" && (
          <div className="space-y-3.5">
            {/* Auto-publish default information banner */}
            <div className="p-2.5 rounded-md bg-success/10 border border-success/20 text-success text-xs flex items-center gap-2">
              <CheckCircle2 size={16} className="shrink-0" />
              <div className="leading-relaxed">
                <strong className="font-semibold">Default Settings:</strong> Modules are automatically <strong>approved, published, free, and visible to students</strong> upon upload. You can change them to premium or hide them later in settings.
              </div>
            </div>

            {/* Hidden multi-file input */}
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".pdf"
              onChange={(event) => handleModuleFilesSelected(event.target.files)}
              className="hidden"
            />

            {/* Drop / Browse Area */}
            <div
              onClick={() => fileInputRef.current?.click()}
              className="border-2 border-dashed border-border hover:border-primary/50 bg-secondary/30 hover:bg-secondary/50 rounded-lg p-5 text-center cursor-pointer transition select-none"
            >
              <div className="flex flex-col items-center justify-center gap-1">
                <Plus className="text-primary" size={24} />
                <p className="text-xs font-bold text-foreground">Click to select PDF module(s)</p>
                <p className="text-xs text-muted-foreground">You can select single or multiple files at once</p>
              </div>
            </div>

            {/* Selected Modules Queue Table */}
            {moduleQueue.length > 0 ? (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="block text-xs font-semibold text-muted-foreground uppercase">
                    Files to upload ({moduleQueue.length})
                  </label>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="text-xs text-primary hover:underline font-semibold flex items-center gap-1 cursor-pointer"
                  >
                    <Plus size={13} /> Add more files
                  </button>
                </div>

                <div className="border border-border rounded-md overflow-hidden max-h-64 overflow-y-auto admin-scroll-y">
                  <table className="w-full text-left text-xs">
                    <thead className="bg-secondary/80 border-b border-border text-xs text-muted-foreground uppercase font-semibold sticky top-0 z-10">
                      <tr>
                        <th className="py-2 px-3 w-8">#</th>
                        <th className="py-2 px-3">File / Size</th>
                        <th className="py-2 px-3">Display Title *</th>
                        <th className="py-2 px-3 w-28">Kind</th>
                        <th className="py-2 px-2 w-10 text-center"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border bg-card">
                      {moduleQueue.map((item, idx) => (
                        <tr key={item.id} className="hover:bg-secondary/20">
                          <td className="py-2.5 px-3 text-muted-foreground font-mono text-xs">
                            {idx + 1}
                          </td>
                          <td className="py-2.5 px-3">
                            <div className="font-semibold text-foreground truncate max-w-[160px]" title={item.file.name}>
                              {item.file.name}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {(item.file.size / (1024 * 1024)).toFixed(2)} MB · <span className="text-success font-medium">Free & Published</span>
                            </div>
                          </td>
                          <td className="py-2.5 px-3">
                            <input
                              type="text"
                              value={item.title}
                              onChange={(e) => handleUpdateQueueItem(item.id, { title: e.target.value })}
                              placeholder="e.g. Chapter 1: Introduction"
                              className="field w-full text-xs px-2.5 py-1.5 rounded-md"
                              required
                            />
                          </td>
                          <td className="py-2.5 px-3">
                            <select
                              value={item.kind}
                              onChange={(e) => handleUpdateQueueItem(item.id, { kind: e.target.value as "module" | "handout" })}
                              className="field w-full text-xs px-2 py-1.5 rounded-md bg-secondary/50"
                            >
                              <option value="module">Module</option>
                              <option value="handout">Handout</option>
                            </select>
                          </td>
                          <td className="py-2.5 px-2 text-center">
                            <button
                              type="button"
                              onClick={() => handleRemoveQueueItem(item.id)}
                              title="Remove"
                              className="p-1 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 cursor-pointer transition"
                            >
                              <Trash2 size={14} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="text-center py-3 text-xs text-muted-foreground border border-dashed border-border/50 rounded-md">
                No files selected yet. Choose one or more PDFs above to configure titles.
              </div>
            )}
          </div>
        )}

        {/* Tab 2: JSON Import */}
        {tab === "import" && (
          <div className="space-y-3.5">
            <div>
              <label className="block text-xs font-semibold text-muted-foreground uppercase mb-1">Import Type</label>
              <select
                value={importType}
                onChange={(e) => setImportType(e.target.value as any)}
                className="field w-full text-xs px-2.5 py-1.5 rounded-md bg-card"
              >
                <option value="past_exam">Past Exam (JSON)</option>
                <option value="quiz">Quiz (JSON)</option>
                <option value="coc">COC Exam (JSON)</option>
                <option value="notes">Notes (Markdown in JSON)</option>
                <option value="content">Chapter Content (Markdown in JSON)</option>
              </select>
            </div>

            {/* Feature 3: Extractor Prompt Generator Panel */}
            {["past_exam", "quiz", "coc"].includes(importType) && (
              <div className="rounded-lg border border-border bg-secondary/40 p-3 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Sparkles size={14} className="text-primary" />
                    <span className="text-xs font-bold text-foreground">Generate Extractor Prompt</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold px-2 py-0.5 rounded bg-primary/10 text-primary border border-primary/20">
                      {activeProfileUsed}
                    </span>
                    <button
                      type="button"
                      onClick={() => setShowPromptPanel((prev) => !prev)}
                      className="text-xs font-medium text-primary hover:underline cursor-pointer"
                    >
                      {showPromptPanel ? "Collapse" : "Expand"}
                    </button>
                  </div>
                </div>

                {showPromptPanel && (
                  <div className="space-y-2.5 pt-1">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                      {/* Choose Course (auto-fills name, type, profile) */}
                      <div>
                        <label className="block text-xs font-semibold text-muted-foreground mb-1">
                          Target Course
                        </label>
                        <select
                          value={promptCourseId}
                          onChange={(e) => setPromptCourseId(e.target.value)}
                          className="field w-full text-xs px-2 py-1.5 rounded-md bg-card"
                        >
                          <option value={courseId}>
                            {courseCode} — {courseName} (Current)
                          </option>
                          {coursesList
                            .filter((c) => c.id !== courseId)
                            .map((c) => (
                              <option key={c.id} value={c.id}>
                                {c.code} — {c.name}
                              </option>
                            ))}
                        </select>
                      </div>

                      {/* Exam Name / Year / Semester */}
                      <div>
                        <label className="block text-xs font-semibold text-muted-foreground mb-1">
                          Exam Name / Year / Semester
                        </label>
                        <input
                          type="text"
                          value={examNameYear}
                          onChange={(e) => setExamNameYear(e.target.value)}
                          placeholder="e.g. 2016 Semester 1 Midterm Exam"
                          className="field w-full text-xs px-2.5 py-1.5 rounded-md"
                        />
                      </div>
                    </div>

                    {/* Question Range */}
                    <div className="grid grid-cols-2 gap-2.5">
                      <div>
                        <label className="block text-xs font-semibold text-muted-foreground mb-1">
                          Question Range: From (Optional)
                        </label>
                        <input
                          type="number"
                          min={1}
                          value={rangeFrom}
                          onChange={(e) => setRangeFrom(e.target.value)}
                          placeholder="e.g. 1"
                          className="field w-full text-xs px-2.5 py-1.5 rounded-md"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-muted-foreground mb-1">
                          Question Range: To (Optional)
                        </label>
                        <input
                          type="number"
                          min={1}
                          value={rangeTo}
                          onChange={(e) => setRangeTo(e.target.value)}
                          placeholder="e.g. 25"
                          className="field w-full text-xs px-2.5 py-1.5 rounded-md"
                        />
                      </div>
                    </div>

                    {/* Read-only prompt box with Copy & character count */}
                    <div className="space-y-1.5 pt-1">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-muted-foreground font-mono">
                          {generatedPrompt.length.toLocaleString()} characters
                        </span>
                        <button
                          type="button"
                          onClick={handleCopyPrompt}
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded bg-secondary hover:bg-secondary/80 text-foreground text-xs font-semibold border border-border transition cursor-pointer"
                        >
                          {promptCopied ? <Check size={12} className="text-success" /> : <Copy size={12} />}
                          <span>{promptCopied ? "Copied Prompt!" : "Copy Prompt"}</span>
                        </button>
                      </div>
                      <textarea
                        readOnly
                        rows={4}
                        value={generatedPrompt}
                        className="field w-full font-mono text-xs p-2.5 rounded-md bg-secondary/50 text-foreground resize-none leading-relaxed select-all"
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            <div>
              <label className="block text-xs font-semibold text-muted-foreground uppercase mb-1">
                Module (Optional: Attach to module in this course)
              </label>
              <select
                value={selectedModuleId}
                onChange={(e) => setSelectedModuleId(e.target.value)}
                className="field w-full text-xs px-2.5 py-1.5 rounded-md bg-card"
              >
                <option value="">-- No module attached / Not applicable --</option>
                {modules.map((m) => (
                  <option key={m.id} value={m.id}>{m.title}</option>
                ))}
              </select>
            </div>

            {/* Feature 4: JSON Payload with Validation & Review Flags */}
            <div className="space-y-2">
              <label className="block text-xs font-semibold text-muted-foreground uppercase mb-1">
                JSON Payload * (Markdown fences supported)
              </label>
              <textarea
                rows={6}
                required
                value={jsonData}
                onChange={(e) => setJsonData(e.target.value)}
                placeholder='{"questions": [...]} or {"markdown": "..."}'
                className="field w-full font-mono text-xs p-2.5 rounded-md"
              />

              {/* Feature 4: Validation feedback */}
              {validation && (
                <div className="space-y-2 pt-1">
                  {/* Needs your review list */}
                  {validation.reviewFlags.length > 0 && (
                    <div className="p-3 rounded-md bg-warning/10 border border-warning/30 text-warning-foreground text-xs space-y-1.5">
                      <div className="font-bold flex items-center gap-1.5 text-warning">
                        <AlertTriangle size={14} className="shrink-0" />
                        <span>Needs your review ({validation.reviewFlags.length} flagged)</span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Review flags returned by AI that require human inspection before publication:
                      </p>
                      <div className="flex flex-wrap gap-1.5 pt-0.5">
                        {validation.reviewFlags.map((flag, idx) => (
                          <span
                            key={idx}
                            className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-warning/20 text-warning border border-warning/30"
                          >
                            {flag}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Errors per question number */}
                  {!validation.valid && (
                    <div className="p-3 rounded-md bg-destructive/10 border border-destructive/20 text-destructive text-xs space-y-1.5">
                      <div className="font-bold flex items-center gap-1.5">
                        <AlertCircle size={14} className="shrink-0" />
                        <span>Validation errors ({validation.errors.length}):</span>
                      </div>
                      <ul className="list-disc list-inside max-h-32 overflow-y-auto admin-scroll-y space-y-0.5 text-xs font-mono leading-relaxed">
                        {validation.errors.map((err, i) => (
                          <li key={i}>{err}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Non-blocking warnings */}
                  {validation.warnings.length > 0 && (
                    <div className="p-2.5 rounded-md bg-secondary/80 border border-border text-xs space-y-1">
                      <div className="font-semibold text-muted-foreground flex items-center gap-1.5">
                        <AlertCircle size={13} className="text-warning shrink-0" />
                        <span>Warnings ({validation.warnings.length} non-blocking notices):</span>
                      </div>
                      <ul className="list-disc list-inside max-h-24 overflow-y-auto admin-scroll-y space-y-0.5 text-xs text-muted-foreground font-mono">
                        {validation.warnings.map((warn, i) => (
                          <li key={i}>{warn}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Success banner */}
                  {validation.valid && (
                    <div className="p-2 rounded-md bg-success/10 border border-success/20 text-success text-xs flex items-center justify-between font-semibold">
                      <span className="flex items-center gap-1.5">
                        <CheckCircle2 size={14} />
                        <span>Valid format: {validation.questionsCount} questions parsed successfully</span>
                      </span>
                      <span className="text-xs opacity-80">Ready to save</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Tab 3: Manual Quiz */}
        {tab === "manual_quiz" && (
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-semibold text-muted-foreground uppercase mb-1">
                Module (Chosen from this course only)
              </label>
              <select
                value={selectedModuleId}
                onChange={(e) => setSelectedModuleId(e.target.value)}
                className="field w-full text-xs px-2.5 py-1.5 rounded-md bg-card"
              >
                <option value="">-- Optional: Attach to a module --</option>
                {modules.map((m) => (
                  <option key={m.id} value={m.id}>{m.title}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-semibold text-muted-foreground uppercase mb-1">
                Questions JSON (Optional)
              </label>
              <textarea
                rows={5}
                value={jsonData}
                onChange={(e) => setJsonData(e.target.value)}
                placeholder='[{"text": "What is...", "options": ["A", "B", "C"], "correct_answer": 0, "explanation": "..."}]'
                className="field w-full font-mono text-xs p-2.5 rounded-md"
              />
            </div>
          </div>
        )}

        <div className="flex items-center justify-between pt-3 border-t border-border">
          <div className="text-xs text-muted-foreground font-medium">
            {uploadProgressText && (
              <span className="flex items-center gap-1.5 text-primary">
                <Loader2 size={13} className="animate-spin shrink-0" />
                <span>{uploadProgressText}</span>
              </span>
            )}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-3 py-1.5 rounded-md border border-border text-xs font-semibold hover:bg-secondary cursor-pointer disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={
                submitting ||
                (tab === "module" && moduleQueue.length === 0) ||
                (tab === "import" && ["past_exam", "quiz", "coc"].includes(importType) && !!(validation && !validation.valid))
              }
              className="px-4 py-1.5 rounded-md bg-primary text-on-accent text-xs font-bold hover:opacity-90 cursor-pointer disabled:opacity-50"
            >
              {submitting
                ? (uploadProgressText || "Uploading…")
                : tab === "module"
                ? (moduleQueue.length > 0 ? `Upload ${moduleQueue.length} Module${moduleQueue.length > 1 ? "s" : ""}` : "Upload Modules")
                : "Add resource"}
            </button>
          </div>
        </div>
      </form>
    </Modal>
  );
}

// ----------------------------------------------------------------------
// Sub-Modal: Generate Notes and Quizzes
// ----------------------------------------------------------------------
function GenerateOrderModal({
  courseId,
  module,
  onClose,
  onSuccess,
}: {
  courseId: string;
  module: AdminCourseModuleItem;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const token = useAdminToken();

  // Auto-detect profile from title
  const detectedProfile = useMemo(() => {
    const t = module.title.toLowerCase();
    if (t.includes("physic") || t.includes("math") || t.includes("circuit") || t.includes("stat")) return "quantitative";
    if (t.includes("chem") || t.includes("bio") || t.includes("econ")) return "mixed";
    if (t.includes("english") || t.includes("afaan") || t.includes("amharic") || t.includes("linguist")) return "language";
    return "descriptive";
  }, [module.title]);

  const [profile, setProfile] = useState<string>(detectedProfile);
  const [questionsPerChapter, setQuestionsPerChapter] = useState(30);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Saved-extraction state
  const [extractionStatus, setExtractionStatus] = useState<SavedExtractionStatusResponse | null>(null);
  const [loadingExtraction, setLoadingExtraction] = useState(false);
  // "scratch" = force re-extract, "saved" = skip extraction
  const [extractionMode, setExtractionMode] = useState<"scratch" | "saved">("scratch");
  const [selectedProvider, setSelectedProvider] = useState("auto");
  const [providerOptions, setProviderOptions] = useState<Array<{ id: string; name: string; enabled: boolean }>>([]);
  const [retryingSectionId, setRetryingSectionId] = useState<string | null>(null);

  // Load saved extraction status when modal opens
  useEffect(() => {
    if (!token) return;
    setLoadingExtraction(true);
    api.admin.savedExtraction
      .getStatus(token, courseId, module.id)
      .then((status) => {
        setExtractionStatus(status);
        if (status.has_saved_extraction && !status.is_outdated) {
          setExtractionMode("saved");
        }
      })
      .catch(() => { /* silently ignore */ })
      .finally(() => setLoadingExtraction(false));

    api.admin.aiProviders
      .list(token)
      .then((rows) => {
        setProviderOptions(rows.map((p) => ({ id: p.id, name: p.name, enabled: p.enabled })));
      })
      .catch(() => { /* keep hardcoded fallback */ });
  }, [token, courseId, module.id]);

  const refreshExtractionStatus = async () => {
    if (!token) return;
    try {
      const status = await api.admin.savedExtraction.getStatus(token, courseId, module.id);
      setExtractionStatus(status);
    } catch {
      /* ignore */
    }
  };

  const handleRetrySection = async (sectionId: string) => {
    if (!token) return;
    setRetryingSectionId(sectionId);
    try {
      await api.admin.savedExtraction.retrySection(token, courseId, module.id, sectionId);
      await refreshExtractionStatus();
    } catch (err: unknown) {
      setErrorMsg(err instanceof Error ? err.message : "Could not retry that section");
    } finally {
      setRetryingSectionId(null);
    }
  };

  const forceReextract = extractionMode === "scratch";
  const useSavedExtraction = extractionMode === "saved";

  // Estimates
  const pages = module.pageCount || 1;
  const estimatedTokens = pages * 800 + questionsPerChapter * 10 * 350;
  const estimatedCostUsd = (estimatedTokens * 0.00000015).toFixed(4);

  const handleCreate = async () => {
    if (!token) return;
    try {
      setSubmitting(true);
      setErrorMsg(null);
      await api.admin.createCourseOrder(token, courseId, module.id, {
        profile,
        questions_per_chapter: questionsPerChapter,
        force_reextract: forceReextract,
        use_saved_extraction: useSavedExtraction,
        provider: selectedProvider,
      });
      onSuccess();
    } catch (err: any) {
      setErrorMsg(err.message || "Failed to create order");
    } finally {
      setSubmitting(false);
    }
  };

  const hasSaved = extractionStatus?.has_saved_extraction ?? false;
  const isOutdated = extractionStatus?.is_outdated ?? false;
  const savedDate = extractionStatus?.meta?.date
    ? new Date(extractionStatus.meta.date).toLocaleDateString("en-GB", {
        day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
      })
    : null;

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title="Generate Notes and Quizzes"
      description={`Module: ${module.title}`}
      size="md"
    >
      <div className="space-y-4 pt-2 text-xs">
        {errorMsg && (
          <div className="p-2.5 rounded-md bg-destructive/10 text-destructive border border-destructive/20">
            {errorMsg}
          </div>
        )}

        <div>
          <label className="block text-xs font-semibold text-muted-foreground uppercase mb-1">
            Subject Profile (Detected: {detectedProfile})
          </label>
          <select
            value={profile}
            onChange={(e) => setProfile(e.target.value)}
            className="field w-full text-xs px-2.5 py-1.5 rounded-md bg-card"
          >
            <option value="quantitative">Quantitative (Math, Physics, Engineering)</option>
            <option value="mixed">Mixed (Chemistry, Biology, Economics)</option>
            <option value="descriptive">Descriptive (History, Philosophy, Law)</option>
            <option value="language">Language (English, Grammar, Linguistics)</option>
          </select>
        </div>

        <div>
          <label className="block text-xs font-semibold text-muted-foreground uppercase mb-1">
            Questions Per Chapter (Default 30)
          </label>
          <input
            type="number"
            min={5}
            max={100}
            value={questionsPerChapter}
            onChange={(e) => setQuestionsPerChapter(Number(e.target.value) || 30)}
            className="field w-full text-xs px-3 py-1.5 rounded-md"
          />
        </div>

        {/* Cost & Token Estimation Box */}
        <div className="p-3 rounded-md bg-secondary/80 border border-border space-y-1">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Document pages:</span>
            <span className="font-bold text-foreground">{pages}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Estimated tokens:</span>
            <span className="font-bold text-foreground">~{estimatedTokens.toLocaleString()}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Estimated cost:</span>
            <span className="font-bold text-primary">${estimatedCostUsd} USD</span>
          </div>
        </div>

        {/* Extraction Mode */}
        <div className="space-y-2">
          <label className="block text-xs font-semibold text-muted-foreground uppercase">
            Extraction
            {loadingExtraction && <Loader2 size={11} className="inline ml-1 animate-spin" />}
          </label>

          {hasSaved && (
            <div className={`p-2.5 rounded-md border text-xs space-y-1 ${isOutdated ? "bg-warning/10 border-warning/30 text-warning" : "bg-secondary border-border text-muted-foreground"}`}>
              {isOutdated && (
                <div className="flex items-center gap-1 font-semibold mb-1">
                  <AlertTriangle size={12} />
                  PDF has changed — saved extraction may be outdated
                </div>
              )}
              <div className="flex gap-3 flex-wrap">
                {savedDate && <span>Saved: <span className="font-semibold text-foreground">{savedDate}</span></span>}
                {extractionStatus?.meta?.extractor_name && (
                  <span>Extractor: <span className="font-semibold text-foreground">{extractionStatus.meta.extractor_name}</span></span>
                )}
                {typeof extractionStatus?.sections_count === "number" && (
                  <span>Sections: <span className="font-semibold text-foreground">{extractionStatus.sections_count}</span></span>
                )}
                {((extractionStatus?.warnings_count ?? extractionStatus?.meta?.warnings?.length) ?? 0) > 0 && (
                  <span className="text-warning">
                    {(extractionStatus?.warnings_count ?? extractionStatus?.meta?.warnings?.length)} warning{(extractionStatus?.warnings_count ?? extractionStatus?.meta?.warnings?.length) !== 1 ? "s" : ""}
                  </span>
                )}
              </div>
              {(extractionStatus?.meta?.warnings || []).slice(0, 4).map((warning) => (
                <p key={warning} className="text-warning">{warning}</p>
              ))}
              {(extractionStatus?.sections || []).filter((s) => s.status === "failed").length > 0 && (
                <div className="pt-1 space-y-1">
                  <p className="font-semibold text-foreground">Failed sections</p>
                  {extractionStatus!.sections.filter((s) => s.status === "failed").map((sec) => (
                    <div key={sec.id} className="flex items-center justify-between gap-2">
                      <span className="text-foreground">{sec.title}</span>
                      <button
                        type="button"
                        onClick={() => handleRetrySection(sec.id)}
                        disabled={retryingSectionId === sec.id}
                        className="inline-flex items-center gap-1 text-primary font-semibold hover:underline cursor-pointer"
                      >
                        <RotateCcw size={12} />
                        {retryingSectionId === sec.id ? "Retrying…" : "Retry section"}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="radio"
              name="extractionMode"
              value="scratch"
              checked={extractionMode === "scratch"}
              onChange={() => setExtractionMode("scratch")}
              className="mt-0.5"
            />
            <span>
              <span className="font-semibold text-foreground">Start from scratch</span>
              <span className="text-muted-foreground ml-1">— re-extract the PDF and overwrite any saved extraction</span>
            </span>
          </label>
          <label className={`flex items-start gap-2 ${hasSaved ? "cursor-pointer" : "cursor-not-allowed opacity-50"}`}>
            <input
              type="radio"
              name="extractionMode"
              value="saved"
              checked={extractionMode === "saved"}
              onChange={() => setExtractionMode("saved")}
              disabled={!hasSaved}
              className="mt-0.5"
            />
            <span>
              <span className="font-semibold text-foreground">Use saved extraction</span>
              <span className="text-muted-foreground ml-1">— skip PDF extraction, run AI only</span>
              {!hasSaved && <span className="text-muted-foreground ml-1">(no saved extraction)</span>}
            </span>
          </label>
        </div>

        {/* AI Provider */}
        <div>
          <label className="block text-xs font-semibold text-muted-foreground uppercase mb-1">
            AI Provider
          </label>
          <select
            value={selectedProvider}
            onChange={(e) => setSelectedProvider(e.target.value)}
            className="field w-full text-xs px-2.5 py-1.5 rounded-md bg-card"
          >
            <option value="auto">Auto (Failover)</option>
            {(providerOptions.length
              ? providerOptions
              : [
                  { id: "gemini", name: "Gemini Flash", enabled: true },
                  { id: "groq", name: "Groq (Llama 3.3 70B)", enabled: true },
                  { id: "openrouter", name: "OpenRouter", enabled: true },
                  { id: "mistral", name: "Mistral", enabled: false },
                  { id: "gemini_openai", name: "Gemini (OpenAI endpoint)", enabled: false },
                  { id: "ollama", name: "Ollama (Local)", enabled: false },
                ]
            ).map((p) => (
              <option key={p.id} value={p.id} disabled={!p.enabled}>
                {p.name}{!p.enabled ? " (Disabled)" : ""}
              </option>
            ))}
          </select>
        </div>

        <div className="flex justify-end gap-2 pt-3 border-t border-border">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 rounded-md border border-border text-xs font-semibold hover:bg-secondary cursor-pointer"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleCreate}
            disabled={submitting}
            className="px-4 py-1.5 rounded-md bg-primary text-on-accent text-xs font-bold hover:opacity-90 cursor-pointer"
          >
            {submitting ? "Creating order…" : "Start generation"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ----------------------------------------------------------------------
// Sub-Modal: Confirm Chapters (Checkbox, editable title & page range, warnings)
// ----------------------------------------------------------------------
function ConfirmChaptersModal({
  courseId,
  order,
  onClose,
  onSuccess,
}: {
  courseId: string;
  order: AdminCourseOrderItem;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const token = useAdminToken();
  const [chapters, setChapters] = useState<AdminCourseOrderChapter[]>(order.chapters || []);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const handleToggleInclude = (idx: number) => {
    setChapters((prev) =>
      prev.map((ch, i) => (i === idx ? { ...ch, include: !ch.include } : ch))
    );
  };

  const handleTitleChange = (idx: number, val: string) => {
    setChapters((prev) =>
      prev.map((ch, i) => (i === idx ? { ...ch, title: val } : ch))
    );
  };

  const handlePageChange = (idx: number, field: "start_page" | "end_page", val: number) => {
    setChapters((prev) =>
      prev.map((ch, i) => (i === idx ? { ...ch, [field]: val } : ch))
    );
  };

  const handleConfirm = async () => {
    if (!token) return;
    try {
      setSubmitting(true);
      setErrorMsg(null);
      await api.admin.confirmCourseOrderChapters(
        token,
        courseId,
        order.moduleId,
        order.id,
        chapters.map((ch) => ({
          id: ch.id,
          idx: ch.idx,
          title: ch.title,
          start_page: ch.start_page,
          end_page: ch.end_page,
          include: ch.include,
        }))
      );
      onSuccess();
    } catch (err: any) {
      setErrorMsg(err.message || "Failed to confirm chapters");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title="Confirm Chapters"
      description={`Review and edit discovered chapters for: ${order.moduleTitle}`}
      size="lg"
    >
      <div className="space-y-4 pt-2 text-xs">
        {errorMsg && (
          <div className="p-2.5 rounded-md bg-destructive/10 text-destructive border border-destructive/20">
            {errorMsg}
          </div>
        )}

        <div className="max-h-[60vh] overflow-y-auto admin-scroll-y space-y-3 pr-1">
          {chapters.map((ch, idx) => (
            <div
              key={ch.id || idx}
              className={`p-3 rounded-md border transition ${ch.include ? "bg-card border-border" : "bg-muted/40 border-border/40 opacity-60"
                }`}
            >
              <div className="flex items-start gap-2.5">
                <input
                  type="checkbox"
                  checked={ch.include}
                  onChange={() => handleToggleInclude(idx)}
                  className="mt-1 cursor-pointer"
                />

                <div className="flex-1 space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="font-mono font-bold text-muted-foreground">Ch.{ch.idx}</span>
                    <input
                      type="text"
                      value={ch.title}
                      onChange={(e) => handleTitleChange(idx, e.target.value)}
                      className="field flex-1 text-xs px-2.5 py-1 rounded-md"
                    />
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-1.5">
                      <span className="text-muted-foreground">Start page:</span>
                      <input
                        type="number"
                        min={1}
                        value={ch.start_page}
                        onChange={(e) => handlePageChange(idx, "start_page", Number(e.target.value))}
                        className="field w-16 text-xs px-2 py-0.5 rounded-md text-center"
                      />
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="text-muted-foreground">End page:</span>
                      <input
                        type="number"
                        min={ch.start_page}
                        value={ch.end_page}
                        onChange={(e) => handlePageChange(idx, "end_page", Number(e.target.value))}
                        className="field w-16 text-xs px-2 py-0.5 rounded-md text-center"
                      />
                    </div>
                  </div>

                  {/* Warnings display */}
                  {ch.warnings && ch.warnings.length > 0 && (
                    <div className="space-y-1 pt-1">
                      {ch.warnings.map((w, wIdx) => (
                        <div key={wIdx} className="flex items-center gap-1.5 text-warning text-xs">
                          <AlertTriangle size={12} className="shrink-0" />
                          <span>{w}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="flex justify-end gap-2 pt-3 border-t border-border">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 rounded-md border border-border text-xs font-semibold hover:bg-secondary cursor-pointer"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={submitting}
            className="px-4 py-1.5 rounded-md bg-primary text-on-accent text-xs font-bold hover:opacity-90 cursor-pointer"
          >
            {submitting ? "Confirming…" : "Confirm chapters & begin"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ----------------------------------------------------------------------
// Sub-Modal: Review Preview Modal
// ----------------------------------------------------------------------
function ReviewPreviewModal({
  item,
  onClose,
  onApprove,
  onDelete,
}: {
  item: AdminCourseReviewItem;
  onClose: () => void;
  onApprove: () => void;
  onDelete: () => void;
}) {
  const p = item.previewData || {};

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={`Preview: ${item.type}`}
      description={item.label}
      size="lg"
    >
      <div className="space-y-4 pt-2 text-xs">
        {/* Flags */}
        {item.flags && item.flags.length > 0 && (
          <div className="space-y-1.5">
            {item.flags.map((f, i) => (
              <div
                key={i}
                className={`p-2 rounded-md border flex items-center gap-2 ${f.type === "blocking"
                  ? "bg-destructive/10 text-destructive border-destructive/20 font-semibold"
                  : "bg-warning/10 text-warning border-warning/20"
                  }`}
              >
                {f.type === "blocking" ? <XCircle size={14} className="shrink-0" /> : <AlertTriangle size={14} className="shrink-0" />}
                <span className="uppercase font-bold">[{f.type}]</span>
                <span>{f.text}</span>
              </div>
            ))}
          </div>
        )}

        {/* Content Preview */}
        <div className="p-3 bg-secondary/50 rounded-md border border-border max-h-[50vh] overflow-y-auto admin-scroll-y space-y-3 font-mono">
          {item.itemType === "quiz" && (
            <div className="space-y-3 font-sans">
              <h4 className="font-bold text-sm text-foreground">{p.title}</h4>
              <p className="text-muted-foreground">Difficulty: {p.difficulty}</p>
              {Array.isArray(p.questions) && p.questions.map((q: any, i: number) => (
                <div key={i} className="p-2.5 rounded-md bg-card border border-border space-y-1">
                  <p className="font-semibold text-foreground">Q{i + 1}: {q.text}</p>
                  <div className="space-y-0.5 pl-2">
                    {Array.isArray(q.options) && q.options.map((opt: string, optIdx: number) => (
                      <p
                        key={optIdx}
                        className={optIdx === q.correct_answer ? "text-success font-bold" : "text-muted-foreground"}
                      >
                        {String.fromCharCode(65 + optIdx)}. {opt} {optIdx === q.correct_answer && "✓"}
                      </p>
                    ))}
                  </div>
                  {q.explanation && (
                    <p className="text-xs text-muted-foreground italic pt-1">
                      Explanation: {q.explanation}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}

          {(item.itemType === "note" || item.itemType === "content") && (
            <div className="space-y-2 font-sans">
              <h4 className="font-bold text-sm text-foreground">{p.title}</h4>
              <div className="whitespace-pre-wrap leading-relaxed text-foreground">
                {p.markdown}
              </div>
            </div>
          )}

          {item.itemType === "module" && (
            <div className="space-y-1 font-sans">
              <h4 className="font-bold text-sm text-foreground">{p.title}</h4>
              <p className="text-muted-foreground">File name: {p.fileName}</p>
              <p className="text-muted-foreground">Pages: {p.pageCount}</p>
              <p className="text-muted-foreground">Size: {p.fileSize} bytes</p>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between pt-3 border-t border-border">
          <button
            type="button"
            onClick={onDelete}
            className="px-3 py-1.5 rounded-md text-destructive hover:bg-destructive/10 font-bold transition cursor-pointer"
          >
            Delete draft
          </button>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1.5 rounded-md border border-border text-xs font-semibold hover:bg-secondary cursor-pointer"
            >
              Close
            </button>
            <button
              type="button"
              onClick={onApprove}
              className={`px-4 py-1.5 rounded-md font-bold text-on-accent cursor-pointer ${item.hasBlocking ? "bg-warning hover:opacity-90" : "bg-success hover:opacity-90"
                }`}
            >
              Approve
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

// ----------------------------------------------------------------------
// Sub-Modal: Edit Course Item Modal (Title, Difficulty, Markdown, Access)
// ----------------------------------------------------------------------
function EditCourseItemModal({
  item,
  loading,
  onClose,
  onSave,
}: {
  item: {
    id: string;
    itemType: string;
    title: string;
    difficulty?: string;
    markdown?: string;
    examYear?: string;
    isFree?: boolean;
  };
  loading: boolean;
  onClose: () => void;
  onSave: (payload: { title: string; difficulty?: string; markdown?: string; examYear?: string; is_free: boolean }) => Promise<void>;
}) {
  const [title, setTitle] = useState(item.title);
  const [isFree, setIsFree] = useState<boolean>(item.isFree !== false);
  const [difficulty, setDifficulty] = useState(item.difficulty || "medium");
  const [examYear, setExamYear] = useState(item.examYear || "");
  const [markdown, setMarkdown] = useState(item.markdown || "");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      title,
      is_free: isFree,
      difficulty: item.itemType === "quiz" ? difficulty : undefined,
      examYear: item.itemType === "exam" ? examYear : undefined,
      markdown: item.itemType === "note" || item.itemType === "content" ? markdown : undefined,
    });
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={`Edit ${item.itemType}`}
      description="Update resource details, access tier, and content"
      size={item.itemType === "note" || item.itemType === "content" ? "lg" : "md"}
    >
      <form onSubmit={handleSubmit} className="space-y-4 pt-2 text-xs">
        <div>
          <label className="block text-xs font-semibold text-muted-foreground uppercase mb-1">Title *</label>
          <input
            type="text"
            required
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="field w-full text-xs px-3 py-2 rounded-md"
          />
        </div>

        {/* Access Tier Toggle */}
        <div className="space-y-1.5 p-3 rounded-md bg-secondary/50 border border-border">
          <label className="block text-xs font-bold text-foreground">Access Tier</label>
          <div className="flex gap-4">
            <label className="flex items-center gap-1.5 cursor-pointer font-semibold">
              <input
                type="radio"
                name="accessTier"
                checked={isFree}
                onChange={() => setIsFree(true)}
              />
              <span className="text-info font-bold">Free</span>
              <span className="text-muted-foreground font-normal">(Accessible to all students)</span>
            </label>
            <label className="flex items-center gap-1.5 cursor-pointer font-semibold">
              <input
                type="radio"
                name="accessTier"
                checked={!isFree}
                onChange={() => setIsFree(false)}
              />
              <span className="text-warning font-bold">PRO / Premium</span>
              <span className="text-muted-foreground font-normal">(Subscribers only)</span>
            </label>
          </div>
        </div>

        {item.itemType === "quiz" && (
          <div>
            <label className="block text-xs font-semibold text-muted-foreground uppercase mb-1">Difficulty</label>
            <select
              value={difficulty}
              onChange={(e) => setDifficulty(e.target.value)}
              className="field w-full text-xs px-3 py-2 rounded-md"
            >
              <option value="easy">Easy</option>
              <option value="medium">Medium</option>
              <option value="hard">Hard</option>
            </select>
          </div>
        )}

        {item.itemType === "exam" && (
          <div>
            <label className="block text-xs font-semibold text-muted-foreground uppercase mb-1">Exam Year</label>
            <input
              type="text"
              value={examYear}
              onChange={(e) => setExamYear(e.target.value)}
              placeholder="e.g. 2024"
              className="field w-full text-xs px-3 py-2 rounded-md"
            />
          </div>
        )}

        {(item.itemType === "note" || item.itemType === "content") && (
          <div>
            <label className="block text-xs font-semibold text-muted-foreground uppercase mb-1">Markdown Notes</label>
            <textarea
              rows={12}
              value={markdown}
              onChange={(e) => setMarkdown(e.target.value)}
              className="field w-full text-xs px-3 py-2 rounded-md font-mono"
            />
          </div>
        )}

        <div className="flex justify-end gap-2 pt-3 border-t border-border">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 rounded-md border border-border text-xs font-semibold hover:bg-secondary cursor-pointer"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-1.5 rounded-md bg-primary text-on-accent text-xs font-bold hover:opacity-90 cursor-pointer"
          >
            {loading ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ----------------------------------------------------------------------
// Sub-Modal: Raw Material Preview Modal
// ----------------------------------------------------------------------
function RawMaterialModal({
  item,
  onClose,
}: {
  item: {
    title: string;
    type: string;
    rawUrl?: string | null;
    markdown?: string;
    questions?: any[];
  };
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);

  const rawText = item.markdown || (item.questions ? JSON.stringify(item.questions, null, 2) : "");

  const handleCopy = () => {
    if (rawText) {
      navigator.clipboard.writeText(rawText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={`Raw Material: ${item.title}`}
      description={`Type: ${item.type}`}
      size="lg"
    >
      <div className="space-y-4 pt-2 text-xs">
        {item.rawUrl && (
          <div className="flex items-center justify-between p-3 rounded-md bg-secondary border border-border">
            <span className="font-semibold text-foreground">Original Document / Source File</span>
            <a
              href={item.rawUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-primary text-on-accent font-bold hover:opacity-90 transition"
            >
              <Download size={13} />
              <span>Download File</span>
            </a>
          </div>
        )}

        {rawText ? (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="font-bold text-muted-foreground uppercase text-xs">Content ({item.type === "quiz" ? "JSON" : "Markdown"})</span>
              <button
                type="button"
                onClick={handleCopy}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded bg-secondary hover:bg-secondary/80 text-foreground font-semibold border border-border cursor-pointer transition"
              >
                {copied ? <Check size={12} className="text-success" /> : <Copy size={12} />}
                <span>{copied ? "Copied!" : "Copy"}</span>
              </button>
            </div>
            <pre className="p-3 bg-secondary/60 rounded-md border border-border max-h-[50vh] overflow-y-auto admin-scroll-y font-mono text-xs whitespace-pre-wrap leading-relaxed text-foreground select-all">
              {rawText}
            </pre>
          </div>
        ) : !item.rawUrl ? (
          <p className="text-muted-foreground italic py-4 text-center">No raw material or downloadable file available for this item.</p>
        ) : null}

        <div className="flex justify-end pt-3 border-t border-border">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-1.5 rounded-md bg-secondary text-foreground text-xs font-semibold hover:bg-secondary/80 cursor-pointer"
          >
            Close
          </button>
        </div>
      </div>
    </Modal>
  );
}
